import stringify from 'json-stable-stringify';
import produce from 'immer';
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import * as BookmarkTypes from 'common/types/reports/bookmark';
import {DisplayBookmark} from 'common/report/dashboards/types';
import {isBehaviorShowClause, isFormulaShowClause} from 'iron/common/report/insights/models/utils';
import {deepOmitNilValues} from 'common/util/objects';
import {ALL_STEPS_FUNNEL_STEP_INDEX_VALUE} from 'common/query-builder/query-block/properties-menu/constants';

import {METRIC_OVERRIDES_PATHS, UI_STATE_BEHAVIOR_FIELDS, UI_STATE_METRIC_FIELDS} from '../constants';
import {isSaveableBehavior} from './behaviors';

/**
 * Returns the canonical form of a Behavior unless it's performing a comparison
 */
export function getSerializedBehaviorDefinition(
  source: BookmarkTypes.Behavior,
  opts: {forComparison?: boolean} = {},
): BookmarkTypes.Behavior {
  if (!opts.forComparison && isSaveableBehavior(source) && source.id && !source.hasUnsavedChanges) {
    return {id: source.id, type: source.type};
  }

  const behavior = {
    ...source,
    behaviors: source.behaviors?.map((subBehavior) => omit(subBehavior, [`_idx`])),
  } satisfies BookmarkTypes.Behavior;

  return omit(behavior, [...UI_STATE_BEHAVIOR_FIELDS, ...(opts.forComparison ? [`hasUnsavedChanges`] : [])]);
}

/**
 * Clauses that reference saved metrics can contain report specific configuration.
 *
 * One example of that is multi-touch attribution selection which is local to a report, it isn't saved as part of a metric definition.
 * To correctly compute when a saved metric is different from its source, we ignore certain fields defined in METRIC_OVERRIDES_PATHS
 *
 * This produces a metric definition which can later be saved in appdbb and used for comparisons
 */
export function getSanitizedClause<T extends BookmarkTypes.ShowClause>(source: T) {
  // NOTE: Due to inconsistent passing of null/undefined values triggered diffs
  // for checking for unsaved changes we normalize the clause by stripping away
  // nil values.
  const overrides = deepOmitNilValues({
    // If source is already in canonical form {id, type, overrides}, extract from source.overrides
    // Otherwise extract from top-level source properties
    ...(source.overrides
      ? source.overrides
      : {
          ...pick(source, METRIC_OVERRIDES_PATHS.clause),
          behavior: isBehaviorShowClause(source) ? pick(source.behavior, METRIC_OVERRIDES_PATHS.behavior) : undefined,
          measurement: pick(source.measurement, METRIC_OVERRIDES_PATHS.measurement),
        }),
  });

  const serialized = deepOmitNilValues({
    ...omit(source, METRIC_OVERRIDES_PATHS.clause),
    behavior: isBehaviorShowClause(source) ? omit(source.behavior, METRIC_OVERRIDES_PATHS.behavior) : undefined,
    measurement: omit(source.measurement, METRIC_OVERRIDES_PATHS.measurement),
    goals: source.goals,
  }) as T;

  // "null" for stepIndex is meaningful and indicates "All Steps" in funnels so
  // we need to persist it.
  if (
    `measurement` in source &&
    source.measurement &&
    `stepIndex` in source.measurement &&
    source.measurement?.stepIndex === ALL_STEPS_FUNNEL_STEP_INDEX_VALUE
  ) {
    (serialized.measurement as BookmarkTypes.BehaviorMeasurement).stepIndex = ALL_STEPS_FUNNEL_STEP_INDEX_VALUE;
  }

  return {
    overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
    serialized,
  };
}

/**
 * This functions serializes a show clause for storing its definition inside a bookmark or comparing
 * it against its canonical Metric definition when referencing an id.
 *
 * The result from this function is used to compute a diff against a referenced Metric or to store
 * a canonical metric id reference inside a show clause where only the id field is persisted.
 */
export function getSerializedClauseDefinition(
  source: BookmarkTypes.ShowClause,
  opts: {forComparison?: boolean} = {},
): BookmarkTypes.ShowClause {
  source = omit(source, UI_STATE_METRIC_FIELDS);
  const {serialized: serializedClause, overrides} = getSanitizedClause(source);

  /** if generating a diff, use serializedClause which omits unnecessary fields for clauses */
  if (opts.forComparison) {
    // overrides fields are report specific and shouldn't be compared
    const comparisonFields = [`hasUnsavedChanges`, `overrides`];

    // when comparing metrics, don't diff the automatic metric names we generate
    if (isBehaviorShowClause(source) && !source.userNamed) {
      comparisonFields.push(`name`);
    }

    return omit(serializedClause, comparisonFields);
  }

  /** return the canonical form of a show clause for bookmark serialization */
  if (source.id && !source.hasUnsavedChanges) {
    return {id: source.id, type: source.type, overrides};
  }

  return source;
}

/**
 * Prepares a given ShowClause for bookmark serialization.
 * This strips away any UI state populated in show clauses.
 */
export function getSerializedClause(
  clause: BookmarkTypes.ShowClause,
  opts: {forComparison?: boolean} = {},
): BookmarkTypes.ShowClause {
  clause = getSerializedClauseDefinition(clause, opts);

  /** formulas with referenced metrics need to be compared by reference when dealing with saved metrics */
  if (isFormulaShowClause(clause) && clause.referencedMetrics) {
    clause.referencedMetrics = clause.referencedMetrics.map(
      (metric) => getSerializedClause(metric, opts) as BookmarkTypes.BehaviorShowClause,
    );
  } else if (isBehaviorShowClause(clause) && clause.behavior) {
    clause.behavior = getSerializedBehaviorDefinition(clause.behavior, opts);
  }

  return clause;
}

/**
 * Compares two ShowClauses to determine if their definitions diverge.
 *
 * This comparison ignores keys that are not relevant, like UI state
 * properties and report-local override fields.
 *
 * @param source - The source ShowClause to compare
 * @param target - The target ShowClause to compare against
 * @returns {boolean} - Returns true if the clause definitions diverge (ignoring specific keys), false otherwise
 */
export function isModifiedMetric(
  source: BookmarkTypes.ShowClause,
  target: BookmarkTypes.ShowClause | undefined,
): boolean {
  if (!target) {
    return false;
  }

  return (
    stringify(getSerializedClause(source, {forComparison: true})) !==
    stringify(getSerializedClause(target, {forComparison: true}))
  );
}

export function serializeBookmarkParams(
  params: BookmarkTypes.InsightsBookmarkParams,
  opts?: {forComparison?: boolean},
) {
  if (!params || `sections` in params === false) {
    return params;
  }

  return produce(params, (draft) => {
    draft.sections.show = draft.sections.show.map((clause) => getSerializedClause(clause, opts));
  });
}

/**
 * Serializes a given bookmark by transforming its `sections.show` clauses
 * into their serialized form using canonical metrics.
 *
 * This also strips away all UI state from the bookmark
 */
export function serializeBookmark(bookmark: DisplayBookmark | null | undefined, opts?: {forComparison?: boolean}) {
  const params = bookmark?.params ?? {};
  if (!bookmark || `sections` in params === false) {
    return bookmark;
  }

  return produce(bookmark as DisplayBookmark<BookmarkTypes.InsightsBookmarkParams>, (draft) => {
    draft.params = serializeBookmarkParams(bookmark.params, opts);
  });
}

/**
 * Compares two bookmarks for equality by serializing them in a comparison-safe format.
 * This strips away UI state and non-essential fields before performing a deep comparison.
 *
 * @param source - The first bookmark to compare
 * @param target - The second bookmark to compare
 * @returns true if the bookmarks are functionally equivalent, false otherwise
 */
export function areBookmarksEqual(
  source: DisplayBookmark | null | undefined,
  target: DisplayBookmark | null | undefined,
) {
  if (!source || !target) {
    return false;
  }

  const bookmarkKeys = [`name`, `description`, `type`, `params`];
  return (
    stringify(serializeBookmark(pick(source, bookmarkKeys) as DisplayBookmark, {forComparison: true})) ===
    stringify(serializeBookmark(pick(target, bookmarkKeys) as DisplayBookmark, {forComparison: true}))
  );
}
